/* eslint-disable max-lines */
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import _ from 'lodash';
import moment from 'moment';

import {
  DAYS_PARAM,
  ITERATION_TYPE,
  ITERATIONS_REGEX_DEFAULT,
  PM_PROVIDERS,
  SETTING_DEFINITION_LEVELS
} from 'common/constants/constants';
import { SettingValues } from 'common/services/settings.service';
import { onSelectionFn, orderIterations } from 'common/utils/utils';
import {
  generatePmImportPayloadFromEntities,
  generatePmImportPayloadFromEntitiesUnderFF,
  getTooManyEntitiesSelectedErrorMessage
} from '../../../../../services/project-management.service';
import { getIterationsFromSync } from 'common/services/iterations.service';
import JiraValidationService from 'components/HOC/PmHOC/JiraValidationService';
import ClubhouseValidationService from 'components/HOC/PmHOC/ClubhouseValidationService';
import AzureBoardsValidationService from 'components/HOC/PmHOC/AzureBoardsValidationService';
import ChipEntityType from '../../PMEntities/ChipEntityType';
import {
  extractSelectedEntitiesFromFilter,
  getProjectsToAddToPmImportStrategyByBoardsIds
} from 'common/services/pm.service';
import { METHOD_NAMES } from 'modals/CreateEditProjectModal/consts';
import useSettingsService from 'components/hooks/useSettingsService';
import { settingsSelector } from 'state/selectors/settings';
import { boardsProviderKeysSelector } from 'state/selectors/boards';
import { FILTER_TYPES } from 'common/constants/filters';
import useEntitiesLimitation from 'views/root/views/home/hooks/useEntitiesLimitation';
import { DeviationError } from 'common/constants/error-types';

const PM_SERVICES = {
  [PM_PROVIDERS.JIRA]: JiraValidationService(),
  [PM_PROVIDERS.CLUBHOUSE]: ClubhouseValidationService(),
  [PM_PROVIDERS.AZURE_BOARDS]: AzureBoardsValidationService()
};
const convertToArrayOfIds = entities =>
  entities?.map(({ provider_id }) => provider_id);

const convertToArrayOfNames = entities => entities?.map(({ name }) => name);

const testRegex = (
  regexTerm,
  setIsSaveEnabled,
  setErrors,
  setFilteredIterations,
  iterations
) => {
  try {
    setErrors([]);
    new RegExp(regexTerm, 'i');
    const regexMatchingIterations = _.chain(iterations)
      .filter(({ name = '' }) =>
        regexTerm ? new RegExp(regexTerm, 'i').test(name.toLowerCase()) : true
      )
      .sortBy('name')
      .value();
    setFilteredIterations(regexMatchingIterations);
  } catch (e) {
    setErrors(['BAD_REGEX']);
    setIsSaveEnabled(false);
  }
};

const getSelectedProjectsSetting = ({
  pmToolConfig,
  getSettingOrDefinitionValue
}) => {
  if (!pmToolConfig?.sensor_id) return [];

  return extractSelectedEntitiesFromFilter[pmToolConfig.sensor_id](
    getSettingOrDefinitionValue({
      name: SettingValues.PM_IMPORT_STRATEGY,
      definitionLevel: SETTING_DEFINITION_LEVELS.organization
    }) || {}
  );
};

const getProjectIterationsSetting = ({
  getSettingOrDefinitionValue,
  selectedTeamId
}) =>
  getSettingOrDefinitionValue({
    name: SettingValues.ITERATION_STRATEGY,
    definitionLevel: SETTING_DEFINITION_LEVELS.team,
    teamId: selectedTeamId
  }) || {};

export const getMergedIterationsList = ({
  iterationsData,
  excludedIterationsIds = []
}) =>
  iterationsData.map(iteration => ({
    ...iteration,
    isSelected: !excludedIterationsIds.includes(iteration.id)
  }));

const onChangeIterationsTypeFn = setIsCustomIterations => event => {
  setIsCustomIterations(event.target.value === ITERATION_TYPE.CUSTOM);
};

export const onTeamProjectSelectionFn =
  (setSelectedProviderIds, setIsLoadingIterations, sensorAuth = {}) =>
  provider_ids => {
    const pmService = _.get(PM_SERVICES, sensorAuth?.sensor_id, {});
    pmService.isSyncPerEntity && setIsLoadingIterations(true);
    setSelectedProviderIds(provider_ids);
  };

export const mergeTwoArraysFn = (first, second) => [
  ...new Set([...first, ...second])
];

const saveToImportSettings = async (
  updateSetting,
  getSettingOrDefinitionValue,
  pmToolConfig,
  selectedProviderIds,
  selectedJiraEntities,
  selectedTeamId,
  selectedShortcutEntities,
  isShortcut,
  allBoardsByProviderIds,
  entitiesDeviation,
  entitiesLimit
) => {
  const providerIdsToSelectedEntitiesMap = {
    [PM_PROVIDERS.JIRA]: selectedJiraEntities,
    [PM_PROVIDERS.CLUBHOUSE]: selectedShortcutEntities
  };
  const pmImportSetting =
    getSettingOrDefinitionValue({
      name: SettingValues.PM_IMPORT_STRATEGY
    }) || {};

  const jiraProjectsToAdd = getProjectsToAddToPmImportStrategyByBoardsIds(
    selectedProviderIds,
    pmImportSetting.projects,
    allBoardsByProviderIds
  );

  const getEntitiesIdsFromPmImportSettings = entityType => {
    const entities = pmImportSetting[entityType] || [];
    return convertToArrayOfIds(entities);
  };
  const getSelectedEntitiesIds = (providerId, entityType) => {
    return (
      convertToArrayOfIds(
        providerIdsToSelectedEntitiesMap[providerId][entityType]
      ) || []
    );
  };
  const selectedEpics = getSelectedEntitiesIds(
    PM_PROVIDERS.JIRA,
    FILTER_TYPES.EPIC
  );

  if (isShortcut) {
    const mergedProjects = mergeTwoArraysFn(
      getEntitiesIdsFromPmImportSettings('projects'),
      getSelectedEntitiesIds(PM_PROVIDERS.CLUBHOUSE, 'projects')
    ).map(item => ({ provider_id: item }));

    const mergedTeams = mergeTwoArraysFn(
      getEntitiesIdsFromPmImportSettings('teams'),
      getSelectedEntitiesIds(PM_PROVIDERS.CLUBHOUSE, 'teams')
    ).map(item => ({ provider_id: item }));
    const payload = { projects: mergedProjects, teams: mergedTeams };
    await updateSetting(SettingValues.PM_IMPORT_STRATEGY, payload);
  } else {
    const mergedBoards = mergeTwoArraysFn(
      getEntitiesIdsFromPmImportSettings('boards'),
      selectedProviderIds
    ).map(item => ({ provider_id: item }));
    const mergedEpics = mergeTwoArraysFn(
      getEntitiesIdsFromPmImportSettings('epics'),
      selectedEpics
    ).map(item => ({ provider_id: item }));

    const payload = {
      ...pmImportSetting,
      boards: mergedBoards,
      epics: mergedEpics,
      projects: jiraProjectsToAdd
    };

    const deviation = entitiesDeviation(mergedBoards);

    if (deviation > 0) {
      throw new DeviationError(
        getTooManyEntitiesSelectedErrorMessage({
          entityName: FILTER_TYPES.BOARD,
          limit: entitiesLimit,
          deviation
        })
      );
    }

    await updateSetting(SettingValues.PM_IMPORT_STRATEGY, payload);
  }
};
const getExcludedIterationsNames = ({ iterations, excludedIterationsIds }) => {
  // This is a temp change until pdt api supports more complex exclude iterations mechanism
  return [];
  // return iterations
  //   .filter(({ id }) => excludedIterationsIds.includes(id))
  //   .map(item => _.get(item, 'name'));
};

export const getCadencePayload = ({
  cadence,
  isTeamPMConfigurationEnabled,
  isCustomIterations
}) =>
  isTeamPMConfigurationEnabled
    ? cadence === METHOD_NAMES.SCRUM.type
      ? METHOD_NAMES.SCRUM
      : METHOD_NAMES.KANBAN
    : isCustomIterations
    ? METHOD_NAMES.KANBAN
    : METHOD_NAMES.SCRUM;

const onSaveSettings = async (
  pmToolConfig,
  selectedTeamProjectId,
  selectedTeamId,
  selectedFilters,
  selectedProviderIds,
  isCustomIterations,
  excludedIterationsIds = [],
  setIsSaveEnabled,
  updateProject,
  regexTerm = ITERATIONS_REGEX_DEFAULT,
  isTeamLicense,
  providerEntities,
  getIterations,
  DateSelectionChanged,
  isTeamPMConfigurationEnabled,
  cadence,
  isShortcut,
  selectedJiraEntities,
  selectedShortcutEntities,
  setIsCustomIterations,
  iterations,
  updateSetting,
  getSettingOrDefinitionValue,
  allBoardsByProviderIds,
  entitiesDeviation,
  entitiesLimit
) => {
  if (!pmToolConfig?.sensor_id) return;

  setIsCustomIterations(cadence === METHOD_NAMES.SCRUM.type);
  const cadencePayload = getCadencePayload({
    cadence,
    isTeamPMConfigurationEnabled,
    isCustomIterations
  });
  if (pmToolConfig.sensor_id === PM_PROVIDERS.AZURE_BOARDS) {
    // convert selectedTeams to boards for common setting:
    const boards = (
      selectedFilters[FILTER_TYPES.AZURE_BOARDS_PROJECT_TEAM_AREAS] || []
    )
      .map(projectTeamAreas => ({
        provider_id: projectTeamAreas?.team?.id,
        name: projectTeamAreas?.team.name
      }))
      .filter(board => !!board?.provider_id);

    await updateProject(selectedTeamProjectId, {
      filter: {
        [FILTER_TYPES.AZURE_BOARDS_PROJECT_TEAM_AREAS]:
          selectedFilters[FILTER_TYPES.AZURE_BOARDS_PROJECT_TEAM_AREAS],
        boards
      },
      metadata: { cadence: cadencePayload },
      sprint_excludes: getExcludedIterationsNames({
        iterations,
        excludedIterationsIds
      }),
      sprint_includes: [regexTerm]
    });
  } else if (isShortcut) {
    // Shortcut
    isTeamPMConfigurationEnabled &&
      (await saveToImportSettings(
        updateSetting,
        getSettingOrDefinitionValue,
        pmToolConfig,
        selectedProviderIds,
        selectedJiraEntities,
        selectedTeamId,
        selectedShortcutEntities,
        isShortcut
      ));
    const shorcutFiltersPayload = {
      [FILTER_TYPES.TEAMS]: selectedShortcutEntities[FILTER_TYPES.TEAMS],
      [FILTER_TYPES.PROJECTS]: selectedShortcutEntities[FILTER_TYPES.PROJECTS],
      [FILTER_TYPES.LABEL]: selectedShortcutEntities[FILTER_TYPES.LABEL],
      [FILTER_TYPES.EPIC]: selectedShortcutEntities[FILTER_TYPES.EPIC]
    };

    let settingPayloadFF;
    if (isTeamPMConfigurationEnabled) {
      settingPayloadFF = shorcutFiltersPayload;
    } else {
      const selectedProviderEntities = providerEntities.filter(
        ({ provider_id }) => selectedProviderIds.includes(provider_id)
      );
      settingPayloadFF = generatePmImportPayloadFromEntities[
        pmToolConfig.sensor_id
      ](selectedProviderEntities);
    }
    await updateProject(selectedTeamProjectId, {
      filter: settingPayloadFF,
      metadata: { cadence: cadencePayload },
      sprint_excludes: getExcludedIterationsNames({
        iterations,
        excludedIterationsIds
      }),
      sprint_includes: [regexTerm]
    });
  } else {
    const selectedBoards = providerEntities.filter(({ provider_id }) =>
      selectedProviderIds.includes(provider_id)
    );

    const dataToSave = {
      boards: selectedJiraEntities[FILTER_TYPES.BOARD],
      labels: selectedJiraEntities[FILTER_TYPES.LABEL],
      fixVersions: selectedJiraEntities[FILTER_TYPES.FIX_VERSION],
      epics: selectedJiraEntities[FILTER_TYPES.EPIC],
      customFields: selectedJiraEntities[FILTER_TYPES.FIELD]
    };

    const settingsPayload = isTeamPMConfigurationEnabled
      ? generatePmImportPayloadFromEntitiesUnderFF[pmToolConfig.sensor_id](
          dataToSave
        )
      : generatePmImportPayloadFromEntities[pmToolConfig.sensor_id](
          selectedBoards
        );
    setIsSaveEnabled(false);
    if (selectedProviderIds && selectedProviderIds.length) {
      const result = await updateProject(selectedTeamProjectId, {
        filter: settingsPayload,
        metadata: { cadence: cadencePayload },
        sprint_excludes: getExcludedIterationsNames({
          iterations,
          excludedIterationsIds
        }),
        sprint_includes: [regexTerm]
      });

      // Check if there is an error in the response
      if (result?.response?.data?.description) {
        throw new DeviationError(result?.response?.data?.description);
      }
    }
  }
  const projectIterationsConfig =
    cadence === METHOD_NAMES.KANBAN.type
      ? {
          customIterations: true,
          iterationLengthInDays: 7,
          iterationEndDate: '2021-01-31'
        }
      : {
          customIterations: false,
          excludedIterationsIds,
          includeIterationsRegex: regexTerm
        };
  await updateSetting(
    SettingValues.ITERATION_STRATEGY,
    projectIterationsConfig,
    'team',
    selectedTeamId
  );
  const teamIterations = await getIterations({ team_id: selectedTeamId });
  DateSelectionChanged(orderIterations(teamIterations)[0]);
};

// eslint-disable-next-line max-lines-per-function
export const usePMIterations = ({
  selectedTeamProjectId,
  projects,
  updateProject,
  selectedTeamId,
  isTeamLicense,
  providerEntities,
  sensorAuth,
  getIterations,
  pmToolConfig,
  retentionDays,
  DateSelectionChanged,
  isTeamPMConfigurationEnabled
}) => {
  const [selectedFilters, setSelectedFilters] = useState({
    board: [],
    label: [],
    fixVersion: [],
    epic: []
  });
  const [errors, setErrors] = useState([]);
  const [autocompleteTerm, setAutocompleteTerm] = useState('');
  const [selectedFiltersOrigin, setSelectedFiltersOrigin] = useState([]);
  const [selectedProviderIds, setSelectedProviderIds] = useState([]);
  const [isSaveEnabled, setIsSaveEnabled] = useState(false);
  const [iterations, setIterations] = useState([]);
  const [filteredIterations, setFilteredIterations] = useState([]);
  const [regexTerm, setRegexTerm] = useState('');
  const [projectIterationsSetting, setProjectIterationsSetting] = useState({});
  const [isCustomIterations, setIsCustomIterations] = useState();
  const [excludedIterationsIds, setExcludedIterationsIds] = useState([]);
  const [iterationsData, setIterationsData] = useState([]);
  const [isLoadingIterations, setIsLoadingIterations] = useState(false);
  const [cadence, setCadence] = useState(METHOD_NAMES.KANBAN.type);
  const [cadenceOrigin, setCadenceOrigin] = useState('');
  const [selectedJiraEntities, setSelectedJiraEntities] = useState([]);
  const [selectedShortcutEntities, setSelectedShortcutEntities] = useState([]);
  const { getSettingOrDefinitionValue, updateSetting } = useSettingsService();
  const allSettings = useSelector(settingsSelector);
  const [isOnSaveAction, setIsOnSaveAction] = useState(false);

  const isShortcut = pmToolConfig?.sensor_id === PM_PROVIDERS.CLUBHOUSE;

  const allBoardsByProviderIds = useSelector(boardsProviderKeysSelector);
  const { entitiesDeviation, entitiesLimit } = useEntitiesLimitation(
    FILTER_TYPES.BOARD
  );

  useEffect(() => {
    if (pmToolConfig?.sensor_id === PM_PROVIDERS.AZURE_BOARDS) return;

    const boardsIds = _.get(selectedFilters, 'board', []).map(item => item.id);
    const boards = _.get(selectedFilters, 'board', []).map(item => ({
      provider_id: item.id,
      name: item.title
    }));
    const epicIds = _.get(selectedFilters, 'epic', []).map(item => ({
      provider_key: item.title,
      provider_id: item.id
    }));

    const labelIds = _.get(selectedFilters, 'label', []).map(item => ({
      name: item.title
    }));

    const fixVersionIds = _.get(selectedFilters, 'fixVersion', []).map(
      item => ({
        provider_id: item.id,
        name: item.title
      })
    );

    const customFields = _.omit(selectedFilters, [
      FILTER_TYPES.BOARD,
      FILTER_TYPES.EPIC,
      FILTER_TYPES.LABEL,
      FILTER_TYPES.FIX_VERSION
    ]);
    const customFieldsArray = Object.entries(customFields || [])
      .map(([key, value]) => ({
        id: key,
        value: value.map(({ id }) => id)
      }))
      .filter(({ value }) => value.length);

    setSelectedProviderIds(boardsIds);
    setSelectedJiraEntities({
      [FILTER_TYPES.BOARD]: boards,
      [FILTER_TYPES.LABEL]: labelIds,
      [FILTER_TYPES.FIX_VERSION]: fixVersionIds,
      [FILTER_TYPES.EPIC]: epicIds,
      [FILTER_TYPES.FIELD]: customFieldsArray
    });
  }, [selectedFilters]);

  const getSelectedEntities = entity =>
    selectedFilters[entity]?.map(item => ({
      provider_id: item.id,
      name: item.title
    }));

  useEffect(() => {
    if (isShortcut) {
      setSelectedShortcutEntities({
        [FILTER_TYPES.TEAMS]: getSelectedEntities(FILTER_TYPES.TEAMS),
        [FILTER_TYPES.PROJECTS]: getSelectedEntities(FILTER_TYPES.PROJECTS),
        [FILTER_TYPES.LABEL]:
          getSelectedEntities(FILTER_TYPES.LABEL) ||
          getSelectedEntities('labels'),
        [FILTER_TYPES.EPIC]:
          getSelectedEntities(FILTER_TYPES.EPIC) || getSelectedEntities('epics')
      });
      setSelectedProviderIds([
        ..._.get(selectedFiltersOrigin, FILTER_TYPES.PROJECTS, []),
        ..._.get(selectedFiltersOrigin, FILTER_TYPES.TEAMS, [])
      ]);
    }
  }, [selectedFilters]);

  const pmImportedProjects = getSelectedProjectsSetting({
    pmToolConfig,
    getSettingOrDefinitionValue
  });
  const providerEntitiesKeys = _.keyBy(providerEntities, 'provider_id');
  const selectedPmImportEntities =
    _.size(providerEntities) > 0
      ? pmImportedProjects.map(({ provider_id }) =>
          _.get(providerEntitiesKeys, provider_id, {})
        )
      : [];

  const projectsFormatedForDropdown = (
    isTeamLicense ? providerEntities : selectedPmImportEntities
  ).map(({ name, provider_id, entityType }) => {
    const displayValue = (
      <>
        {name}
        <ChipEntityType entityType={entityType} />
      </>
    );
    return { value: provider_id, displayValue, searchableValue: name };
  });

  useEffect(() => {
    const doSync = async (sensorAuth, selectedProviderIds) => {
      try {
        const iterationsFromSync = await getIterationsFromSync({
          sensorAuth,
          selectedProviderIds
        });
        setIterationsData(iterationsFromSync.iterations);
      } catch (e) {
      } finally {
        setIsLoadingIterations(false);
      }
    };

    if (sensorAuth && sensorAuth?.sensor_id) {
      const pmService = _.get(PM_SERVICES, sensorAuth.sensor_id, {
        handleSync: () => {}
      });
      pmService.handleSync({
        sensorAuth,
        iterationsData,
        doSync,
        selectedProviderIds,
        setIterationsData,
        setIsLoadingIterations,
        selectedFilters
      });
    }
  }, [
    sensorAuth,
    selectedProviderIds,
    selectedFilters[FILTER_TYPES.AZURE_BOARDS_PROJECT_TEAM_AREAS]
  ]);
  useEffect(() => {
    setProjectIterationsSetting(
      getProjectIterationsSetting({
        getSettingOrDefinitionValue,
        selectedTeamId
      })
    );
  }, [allSettings, selectedTeamId]);

  useEffect(() => {
    const teamProject = projects.find(({ id }) => id === selectedTeamProjectId);
    const teamEntitiesFilter = _.get(teamProject, 'filter', {});
    if (!pmToolConfig?.sensor_id) return;

    if (pmToolConfig.sensor_id === PM_PROVIDERS.AZURE_BOARDS) {
      const defaultFilters = {
        [FILTER_TYPES.AZURE_BOARDS_PROJECT_TEAM_AREAS]:
          teamEntitiesFilter[FILTER_TYPES.AZURE_BOARDS_PROJECT_TEAM_AREAS] || []
      };
      setSelectedFiltersOrigin(defaultFilters);
      setSelectedFilters(defaultFilters);
    } else if (isShortcut) {
      setSelectedFiltersOrigin(teamEntitiesFilter);
    } else {
      const teamEntitiesEpicFilter = _.get(teamEntitiesFilter, 'epics', []);
      const teamEntitiesLabelFilter = _.get(teamEntitiesFilter, 'labels', []);
      const teamEntitiesFixVersionFilter = _.get(
        teamEntitiesFilter,
        'fixVersions',
        []
      );
      const teamCustomFieldsFilter = _.get(teamEntitiesFilter, 'fields', []);
      const selectedEntities =
        extractSelectedEntitiesFromFilter[pmToolConfig.sensor_id](
          teamEntitiesFilter
        );

      const selectedEntitiesBoardsIds = selectedEntities.map(
        entity => entity.provider_id
      );

      const selectedEntitiesFixVersionIds = teamEntitiesFixVersionFilter.map(
        fixVersion => fixVersion.name
      );

      setSelectedProviderIds(selectedEntitiesBoardsIds);

      const selectedFromOrigin = {
        [FILTER_TYPES.BOARD]: selectedEntities,
        [FILTER_TYPES.EPIC]: teamEntitiesEpicFilter,
        [FILTER_TYPES.LABEL]: teamEntitiesLabelFilter,
        [FILTER_TYPES.FIX_VERSION]: selectedEntitiesFixVersionIds,
        [FILTER_TYPES.FIELD]: teamCustomFieldsFilter
      };

      setSelectedFiltersOrigin(selectedFromOrigin);
    }

    const cadenceFromDB = _.get(
      teamProject,
      'metadata.cadence.type',
      METHOD_NAMES.KANBAN.type
    );

    setCadenceOrigin(cadenceFromDB);
    setCadence(cadenceFromDB);
  }, [projects, selectedTeamProjectId]);

  useEffect(() => {
    // Merged list of settings and iterations with isSelected prop
    const mergedIterations = getMergedIterationsList({
      iterationsData,
      excludedIterationsIds: iterations.length
        ? iterations.filter(({ isSelected }) => !isSelected).map(({ id }) => id)
        : projectIterationsSetting.excludedIterationsIds
    });

    setIterations(mergedIterations);
    setFilteredIterations(mergedIterations);
  }, [projectIterationsSetting, iterationsData]);

  useEffect(() => {
    setIsCustomIterations(projectIterationsSetting.customIterations);
    setRegexTerm(projectIterationsSetting.includeIterationsRegex);
    setExcludedIterationsIds(projectIterationsSetting.excludedIterationsIds);
  }, [projectIterationsSetting]);

  // Update FilteredIterations list
  useEffect(() => {
    testRegex(
      regexTerm,
      setIsSaveEnabled,
      setErrors,
      setFilteredIterations,
      iterations
    );
  }, [regexTerm]);

  // When iterations updated:
  useEffect(() => {
    // update filtered projects as well
    setFilteredIterations(
      _.chain(iterations)
        .filter(({ name = '' }) =>
          regexTerm ? new RegExp(regexTerm, 'i').test(name.toLowerCase()) : true
        )
        .orderBy([({ end_date }) => new Date(end_date)], ['desc'])
        .value()
    );

    // update excludedIterations
    setExcludedIterationsIds(
      iterations.filter(({ isSelected }) => !isSelected).map(({ id }) => id)
    );
  }, [iterations]);

  const checkIfChanged = (currentSelected, originSelected) =>
    !_.isEqual(currentSelected?.sort(), originSelected?.sort());

  const checkIterationsChanges = () => {
    const isExcludedIterationsChanged = !_.isEqual(
      projectIterationsSetting.excludedIterationsIds &&
        projectIterationsSetting.excludedIterationsIds?.sort(),
      excludedIterationsIds && excludedIterationsIds?.sort()
    );
    const isIncludeIterationsRegexChanged =
      projectIterationsSetting.includeIterationsRegex !== regexTerm;

    const isIterationsChanged =
      !isCustomIterations &&
      (isExcludedIterationsChanged || isIncludeIterationsRegexChanged);

    const selectedIterations = orderIterations(filteredIterations).filter(
      ({ isSelected }) => isSelected
    );

    // found at least one selected iterations in retention days
    const isSelectedIterationsInRetention = selectedIterations
      .map(({ end_date }) =>
        moment().isAfter(
          moment(end_date).add(retentionDays, DAYS_PARAM).startOf('day')
        )
      )
      .some(i => !i);

    const selectedIterationsInRetention = retentionDays
      ? isSelectedIterationsInRetention
      : !!selectedIterations.length;

    const isIterationsNotCustom =
      isCustomIterations || selectedIterationsInRetention;

    return (
      isIterationsNotCustom &&
      (isCustomIterations !== projectIterationsSetting.customIterations ||
        isIterationsChanged)
    );
  };

  const checkJiraFiltersChanges = () => {
    if (isTeamPMConfigurationEnabled) {
      return (
        !_.isEqual(selectedJiraEntities, selectedFiltersOrigin) &&
        selectedJiraEntities[FILTER_TYPES.BOARD].length
      );
    }
    const selectedProviderIdsOrigin = (
      selectedFiltersOrigin[FILTER_TYPES.BOARD] || []
    ).map(({ provider_id }) => provider_id);
    return (
      !_.isEqual(selectedProviderIdsOrigin, selectedProviderIds) &&
      (selectedProviderIds || []).length
    );
  };

  const checkShortcutFiltersChanges = () => {
    if (isTeamPMConfigurationEnabled) {
      let oneFilterChanged = false;
      Object.keys(selectedShortcutEntities).forEach(keyName => {
        if (
          checkIfChanged(
            keyName === FILTER_TYPES.LABEL
              ? convertToArrayOfNames(selectedShortcutEntities[keyName])
              : convertToArrayOfIds(selectedShortcutEntities[keyName]),
            selectedFiltersOrigin[keyName]
          )
        )
          oneFilterChanged = true;
      });
      return oneFilterChanged;
    } else {
      const selectedProviderIdsOrigin = [
        ..._.get(selectedFiltersOrigin, FILTER_TYPES.PROJECTS, []),
        ..._.get(selectedFiltersOrigin, FILTER_TYPES.TEAMS, [])
      ];
      return checkIfChanged(selectedProviderIdsOrigin, selectedProviderIds);
    }
  };

  const checkAzureBoardsFiltersChanges = () =>
    !_.isEmpty(
      selectedFilters?.[FILTER_TYPES.AZURE_BOARDS_PROJECT_TEAM_AREAS]
    ) &&
    JSON.stringify(selectedFilters) !== JSON.stringify(selectedFiltersOrigin);

  const mapCheckPmFiltersChanges = {
    [PM_PROVIDERS.JIRA]: checkJiraFiltersChanges,
    [PM_PROVIDERS.CLUBHOUSE]: checkShortcutFiltersChanges,
    [PM_PROVIDERS.AZURE_BOARDS]: checkAzureBoardsFiltersChanges
  };

  // check if save should be enabled
  useEffect(() => {
    const cadenceChanged = cadence !== cadenceOrigin;
    const hasSelectedIterations = !!(iterations || []).filter(
      ({ isSelected }) => isSelected
    ).length;
    const iterationsChanged = checkIterationsChanges();
    const isSaveEnabled =
      mapCheckPmFiltersChanges[pmToolConfig.sensor_id]() ||
      (hasSelectedIterations && iterationsChanged) ||
      (cadenceChanged && selectedProviderIds?.length);

    setIsSaveEnabled(isSaveEnabled);
  }, [
    projectIterationsSetting,
    selectedFiltersOrigin[FILTER_TYPES.BOARD]?.sort().toString(),
    selectedProviderIds?.sort().toString(),
    selectedFilters,
    regexTerm,
    isCustomIterations,
    excludedIterationsIds,
    filteredIterations,
    cadence,
    selectedJiraEntities,
    selectedShortcutEntities
  ]);

  return {
    errors,
    selectedProviderIds,
    pmImportedProjects,
    projectsFormatedForDropdown,
    iterations,
    filteredIterations,
    setAutocompleteTerm,
    autocompleteTerm,
    setRegexTerm,
    regexTerm,
    isCustomIterations,
    isSaveEnabled,
    isLoadingIterations,
    selectedJiraEntities,
    selectedShortcutEntities,
    cadence,
    setCadence,
    onChangeCadence: setIsCustomIterations,
    onChangeIterationsType: onChangeIterationsTypeFn(setIsCustomIterations),
    onIterationSelection: onSelectionFn(setIterations, iterations, 'id'),
    onTeamProjectSelection: onTeamProjectSelectionFn(
      setSelectedProviderIds,
      setIsLoadingIterations,
      sensorAuth
    ),
    selectedFilters,
    setSelectedFilters,
    selectedFiltersOrigin,
    isOnSaveAction,
    setIsOnSaveAction,
    onSaveChangesClick: () =>
      onSaveSettings(
        pmToolConfig,
        selectedTeamProjectId,
        selectedTeamId,
        selectedFilters,
        selectedProviderIds,
        isCustomIterations,
        excludedIterationsIds,
        setIsSaveEnabled,
        updateProject,
        _.isEmpty(regexTerm) ? ITERATIONS_REGEX_DEFAULT : regexTerm,
        isTeamLicense,
        providerEntities,
        getIterations,
        DateSelectionChanged,
        isTeamPMConfigurationEnabled,
        cadence,
        isShortcut,
        selectedJiraEntities,
        selectedShortcutEntities,
        setIsCustomIterations,
        iterations,
        updateSetting,
        getSettingOrDefinitionValue,
        allBoardsByProviderIds,
        entitiesDeviation,
        entitiesLimit
      )
  };
};
